import React, { useEffect, useMemo, useRef, useState } from 'react';
import { AlertCircle, Filter, Loader2, RefreshCw, SearchX, Sparkles } from 'lucide-react';
import { useToast } from '../../context/ToastContext';
import { useProspecting } from '../../context/ProspectingContext';
import PersonCard from './PersonCard';
import CompanyCard from './CompanyCard';
import AppliedFilterChip from './AppliedFilterChip';
import ProspectingIcpBanner from './ProspectingIcpBanner';
import useFLIPGrid from './useFLIPGrid';
import {
  PROSPECTING_PREVIEW_LIMIT,
  loadProspectingPreview,
  mapLeadPreviewRows,
} from './prospectingApi';

function EmptyResult({ onClearFilters, hasFilters }) {
  return (
    <div className="flex-1 flex items-center justify-center">
      <div className="max-w-sm text-center px-6">
        <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-gray-100 dark:bg-gray-800 mb-3">
          <SearchX className="w-5 h-5 text-gray-400" />
        </div>
        <div className="text-sm font-semibold text-gray-700 dark:text-gray-200 mb-1">
          No matches
        </div>
        <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
          {hasFilters
            ? 'Your filters narrowed the list down to zero. Try removing one or asking Able to broaden the search.'
            : 'There’s nothing here yet.'}
        </p>
        {hasFilters && (
          <button
            type="button"
            onClick={onClearFilters}
            className="inline-flex items-center gap-1.5 rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-1.5 text-xs font-medium text-gray-700 dark:text-gray-200 hover:border-brand-300 dark:hover:border-brand-700 transition-colors"
          >
            Clear all filters
          </button>
        )}
      </div>
    </div>
  );
}

function LoadingResult({ deepSearch = false }) {
  return (
    <div className="flex-1 overflow-y-auto p-6">
      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        {Array.from({ length: 4 }).map((_, index) => (
          <div
            key={index}
            className="h-[176px] rounded-lg border border-gray-100 dark:border-gray-800 bg-white dark:bg-gray-900 p-4"
          >
            <div className="flex items-start gap-3.5">
              <div className="w-10 h-10 rounded-full bg-gray-100 dark:bg-gray-800 animate-pulse" />
              <div className="flex-1 space-y-2 pt-1">
                <div className="h-4 w-40 rounded bg-gray-100 dark:bg-gray-800 animate-pulse" />
                <div className="h-3 w-56 rounded bg-gray-100 dark:bg-gray-800 animate-pulse" />
              </div>
            </div>
            <div className="mt-5 h-3 w-64 rounded bg-gray-100 dark:bg-gray-800 animate-pulse" />
            <div className="mt-5 flex gap-2">
              <div className="h-6 w-24 rounded-full bg-gray-100 dark:bg-gray-800 animate-pulse" />
              <div className="h-6 w-28 rounded-full bg-gray-100 dark:bg-gray-800 animate-pulse" />
            </div>
            <div className="mt-5 flex gap-2">
              <div className="h-8 flex-1 rounded-md bg-gray-100 dark:bg-gray-800 animate-pulse" />
              <div className="h-8 flex-1 rounded-md bg-gray-100 dark:bg-gray-800 animate-pulse" />
            </div>
          </div>
        ))}
      </div>
      {deepSearch ? (
        <div className="mt-4 mx-auto max-w-md flex items-start gap-2 rounded-lg border border-brand-100 dark:border-brand-900/40 bg-brand-50/60 dark:bg-brand-950/30 px-4 py-3">
          <Sparkles className="w-4 h-4 text-brand-500 flex-shrink-0 mt-0.5" />
          <div className="text-xs text-brand-700 dark:text-brand-200">
            <div className="font-semibold mb-0.5">Searching for fresh leads…</div>
            <p className="text-brand-600/90 dark:text-brand-300/90">
              Our AI prospecting agent is finding companies that match your filters. This usually takes 30–45 seconds.
            </p>
          </div>
        </div>
      ) : (
        <div className="mt-4 flex items-center justify-center gap-2 text-xs text-gray-400 dark:text-gray-500">
          <Loader2 className="w-3.5 h-3.5 animate-spin" />
          Loading prospect preview
        </div>
      )}
    </div>
  );
}

const UNSUPPORTED_FILTER_LABELS = {
  employee_count: 'Employee count',
  employee_count_ranges: 'Company size',
  founded_year: 'Founded year',
  technologies_any: 'Technologies',
  company_tags_any: 'Company tags',
  highlights_any: 'Highlights',
  seniorities: 'Seniority',
  functions: 'Function',
  titles_any: 'Job titles',
  email_statuses: 'Email status',
  'company.industries': 'Company industry',
  'company.country_codes': 'Company country',
  'company.employee_count': 'Employee count',
  'company.technologies_any': 'Technologies',
  'filters:too_broad': 'a more specific industry or city',
  'entity_type:person': null,
};

function UnsupportedFiltersBanner({ unsupportedFilters }) {
  const removable = unsupportedFilters
    .map((key) => UNSUPPORTED_FILTER_LABELS[key])
    .filter(Boolean);

  if (removable.length === 0) return null;

  return (
    <div className="flex-shrink-0 mx-6 mt-4 -mb-2 flex items-start gap-2 rounded-lg border border-amber-200 dark:border-amber-900/40 bg-amber-50 dark:bg-amber-950/30 px-3 py-2.5">
      <AlertCircle className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
      <div className="text-xs text-amber-800 dark:text-amber-200">
        <span className="font-semibold">Not many results? </span>
        Some filters can&apos;t be used to find fresh leads:&nbsp;
        <span className="font-medium">{formatList(removable)}</span>. Try removing them to broaden your search.
      </div>
    </div>
  );
}

function formatList(items) {
  const unique = Array.from(new Set(items));
  if (unique.length === 0) return '';
  if (unique.length === 1) return unique[0];
  if (unique.length === 2) return `${unique[0]} and ${unique[1]}`;
  return `${unique.slice(0, -1).join(', ')}, and ${unique[unique.length - 1]}`;
}

function ErrorResult({ message, onRetry }) {
  return (
    <div className="flex-1 flex items-center justify-center">
      <div className="max-w-sm text-center px-6">
        <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-rose-50 dark:bg-rose-950/30 mb-3">
          <AlertCircle className="w-5 h-5 text-rose-500" />
        </div>
        <div className="text-sm font-semibold text-gray-700 dark:text-gray-200 mb-1">
          Preview unavailable
        </div>
        <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
          {message || 'The prospecting data service could not be reached.'}
        </p>
        <button
          type="button"
          onClick={onRetry}
          className="inline-flex items-center gap-1.5 rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-1.5 text-xs font-medium text-gray-700 dark:text-gray-200 hover:border-brand-300 dark:hover:border-brand-700 transition-colors"
        >
          <RefreshCw className="w-3 h-3" />
          Retry
        </button>
      </div>
    </div>
  );
}

export default function ProspectingPreviewPanel({
  search,
  wideGrid = false,
  onBuildList,
  savingList = false,
  onPreviewResponse,
}) {
  const { showInfo, showSuccess } = useToast();
  const { removeFilter, clearFilters } = useProspecting();
  const [reloadToken, setReloadToken] = useState(0);
  const [previewState, setPreviewState] = useState({
    loading: true,
    error: null,
    response: null,
    records: [],
  });
  const [deepSearchActive, setDeepSearchActive] = useState(false);

  useEffect(() => {
    if (!previewState.loading) {
      setDeepSearchActive(false);
      return undefined;
    }
    const timer = setTimeout(() => setDeepSearchActive(true), 1500);
    return () => clearTimeout(timer);
  }, [previewState.loading]);
  const previewSearch = useMemo(
    () => ({ type: search.type, filters: search.filters }),
    [search.type, search.filters]
  );

  useEffect(() => {
    const controller = new AbortController();
    setPreviewState({
      loading: true,
      error: null,
      response: null,
      records: [],
    });
    onPreviewResponse?.(null);

    loadProspectingPreview(previewSearch, {
      signal: controller.signal,
      limit: PROSPECTING_PREVIEW_LIMIT,
    })
      .then(({ preview }) => {
        setPreviewState({
          loading: false,
          error: null,
          response: preview,
          records: mapLeadPreviewRows(preview.items),
        });
        onPreviewResponse?.(preview);
      })
      .catch((error) => {
        if (error.name === 'AbortError') return;
        setPreviewState({
          loading: false,
          error: error.message,
          response: null,
          records: [],
        });
        onPreviewResponse?.(null);
      });

    return () => controller.abort();
  }, [onPreviewResponse, previewSearch, reloadToken]);

  const visible = previewState.records;
  const flipKeys = useMemo(
    () => `${search.type}:${wideGrid ? 'wide' : 'standard'}:${visible.map((r) => r.id).join('|')}`,
    [search.type, visible, wideGrid]
  );

  // Track which record ids were already present in the previous render so brand-new
  // cards animate in, while cards that just shifted position don't restart their entry.
  const prevIdsRef = useRef(new Set());
  const setItemRef = useFLIPGrid(flipKeys);

  useEffect(() => {
    prevIdsRef.current = new Set();
  }, [search.id, search.type]);

  const handleFindEmail = (person) => {
    showInfo(`Email lookup for ${person.name} — coming soon`);
  };
  const handleFindPhone = (person) => {
    showInfo(`Phone lookup for ${person.name} — coming soon`);
  };
  const handleAddPersonToCRM = (person) => {
    showSuccess(`${person.name} added to CRM (mock)`);
  };

  return (
    <main className="flex-1 flex flex-col overflow-hidden bg-white dark:bg-gray-900">
      <ProspectingIcpBanner
        icp={search.icp}
        onBuildList={onBuildList}
        savingList={savingList}
      />

      {/* Sticky header: filters (left, wraps) */}
      <div className="flex-shrink-0 px-6 py-3 border-b border-gray-100 dark:border-gray-700 bg-white/95 dark:bg-gray-900/95 backdrop-blur-sm">
        <div className="flex items-start gap-3 min-w-0">
          {/* Filters label + chips (or empty-state hint) */}
          <div className="flex items-start gap-3 flex-1 min-w-0">
            <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500 pt-1 flex-shrink-0">
              <Filter className="w-3 h-3" /> Filters
            </div>
            <div className="flex flex-wrap gap-1.5 min-h-[28px] items-center flex-1 min-w-0">
              {search.filters.length === 0 ? (
                <span className="text-xs text-gray-400 dark:text-gray-500 italic">
                  None applied — ask Able on the right to narrow this list
                </span>
              ) : (
                <>
                  {search.filters.map((f) => (
                    <AppliedFilterChip
                      key={f.id}
                      filter={f}
                      onRemove={(id) => removeFilter(search.id, id)}
                    />
                  ))}
                  <button
                    type="button"
                    onClick={() => clearFilters(search.id)}
                    className="text-[11px] font-medium text-gray-500 dark:text-gray-400 hover:text-brand-600 dark:hover:text-brand-400 transition-colors ml-1"
                  >
                    Clear all
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {!previewState.loading && !previewState.error && (
        <UnsupportedFiltersBanner
          unsupportedFilters={previewState.response?.live_lookup_unsupported_filters || []}
        />
      )}

      {/* Cards grid */}
      {previewState.loading && visible.length === 0 ? (
        <LoadingResult deepSearch={deepSearchActive} />
      ) : previewState.error ? (
        <ErrorResult
          message={previewState.error}
          onRetry={() => setReloadToken((value) => value + 1)}
        />
      ) : visible.length === 0 ? (
        <EmptyResult
          hasFilters={search.filters.length > 0}
          onClearFilters={() => clearFilters(search.id)}
        />
      ) : (
        <div className="flex-1 overflow-y-auto p-6">
          <div
            key={search.type}
            className={`grid grid-cols-1 gap-4 ${wideGrid ? 'xl:grid-cols-3' : 'xl:grid-cols-2'}`}
          >
            {visible.map((record, i) => {
              const isNew = !prevIdsRef.current.has(record.id);
              return (
                <div
                  key={record.id}
                  ref={setItemRef(record.id)}
                  className={isNew ? 'animate-cardEnter' : ''}
                  style={isNew ? { animationDelay: `${i * 35}ms`, animationFillMode: 'both' } : undefined}
                >
                  {search.type === 'companies' ? (
                    <CompanyCard
                      company={record}
                    />
                  ) : (
                    <PersonCard
                      person={record}
                      onFindEmail={handleFindEmail}
                      onFindPhone={handleFindPhone}
                      onAddToCRM={handleAddPersonToCRM}
                    />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
      <CommitVisibleIds visible={visible} prevIdsRef={prevIdsRef} />
    </main>
  );
}

// Helper component: runs after render to record the current visible set so the
// NEXT render can distinguish "new" cards from "shifted" cards.
function CommitVisibleIds({ visible, prevIdsRef }) {
  React.useEffect(() => {
    prevIdsRef.current = new Set(visible.map((r) => r.id));
  }, [visible, prevIdsRef]);
  return null;
}
